import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { getSupabase } from '../lib/supabase';
import { themeLogger as logger } from '../lib/logger';

export type Theme = 'office' | 'resto' | 'nightclub';

interface ThemeContextType {
  theme: 'office' | 'resto' | 'nightclub';
  setTheme: (theme: 'office' | 'resto' | 'nightclub') => void;
  colorScheme: 'light' | 'dark';
  setColorScheme: (scheme: 'light' | 'dark') => void;
  isDark: boolean;
  toggleDarkMode: () => void;
  forceTheme: (newTheme: 'office' | 'resto' | 'nightclub') => void;
  // Ultra-dark black surface control
  surface: 'default' | 'black';
  setSurface: (surface: 'default' | 'black') => void;
  isBlack: boolean;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: React.ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [theme, setTheme] = useState<'office' | 'resto' | 'nightclub'>('office');
  const [colorScheme, setColorScheme] = useState<'light' | 'dark'>('light');
  const [surface, setSurfaceState] = useState<'default' | 'black'>('default');
  const { user } = useAuth();

  // Refs pour éviter les boucles infinies (intégration de useThemeSync)
  const isInitializedRef = React.useRef(false);
  const lastUserThemeRef = React.useRef<string | null>(null);

  // 🎯 Fonction de détection du thème depuis l'URL (SANS dépendances pour éviter les boucles)
  const detectThemeFromUrl = React.useCallback(() => {
    const pathname = window.location.pathname;
    const pathParts = pathname.split('/').filter(Boolean);
    
    logger.debug('Detecting theme from URL:', { href: window.location.href, pathname, pathParts });
    
    const skinFromUrl = pathParts.find(part => ['resto', 'office', 'nightclub'].includes(part)) as Theme | undefined;
    
    if (skinFromUrl) {
      logger.debug(`Theme found in URL: ${skinFromUrl}`);
      setTheme(skinFromUrl);
      localStorage.setItem('komby-theme', skinFromUrl); // Persister le skin
      lastUserThemeRef.current = skinFromUrl;
      
      // Skin URL → mode : Hospitality + Entertainment = Moon ; Office = Sun
      if (skinFromUrl === 'nightclub' || skinFromUrl === 'resto') {
        setColorScheme('dark');
        setSurfaceState('default');
        document.documentElement.classList.add('dark');
        document.documentElement.classList.remove('theme-black');
      } else if (skinFromUrl === 'office') {
        setColorScheme('light');
        setSurfaceState('default');
        document.documentElement.classList.remove('dark');
        document.documentElement.classList.remove('theme-black');
      }
      
      return skinFromUrl;
    } else {
      // Pas de skin dans l'URL → Charger depuis localStorage ou utiliser le défaut
      const savedTheme = localStorage.getItem('komby-theme') as Theme | null;
      const savedColorScheme = localStorage.getItem('komby-color-scheme') as 'light' | 'dark' | null;
      const savedSurface = localStorage.getItem('komby-surface') as 'default' | 'black' | null;
      
      // Load skin from localStorage
      if (savedTheme && ['resto', 'office', 'nightclub'].includes(savedTheme)) {
        logger.debug(`Skin loaded from localStorage: ${savedTheme}`);
        setTheme(savedTheme);
        lastUserThemeRef.current = savedTheme;
      }
      
      if (savedColorScheme || savedSurface) {
        logger.debug(`Loading from localStorage: colorScheme=${savedColorScheme}, surface=${savedSurface}`);
        
        if (savedColorScheme) {
          setColorScheme(savedColorScheme);
          if (savedColorScheme === 'dark') {
            document.documentElement.classList.add('dark');
          } else {
            document.documentElement.classList.remove('dark');
          }
        }
        
        if (savedSurface) {
          setSurfaceState(savedSurface);
          if (savedSurface === 'black') {
            document.documentElement.classList.add('theme-black');
          } else {
            document.documentElement.classList.remove('theme-black');
          }
        }
      } else {
        // No localStorage → default light
        logger.debug('No saved theme, using default: light');
        setColorScheme('light');
        setSurfaceState('default');
        document.documentElement.classList.remove('dark');
        document.documentElement.classList.remove('theme-black');
      }
      
      return savedTheme || 'office';
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Initialize on mount
  useEffect(() => {
    logger.debug('ThemeContext mounted - Initializing...');
    detectThemeFromUrl();
    isInitializedRef.current = true;

    const handleUrlChange = () => {
      logger.debug('URL changed, detecting new theme...');
      detectThemeFromUrl();
    };

    window.addEventListener('popstate', handleUrlChange);
    
    return () => {
      window.removeEventListener('popstate', handleUrlChange);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Charger le thème depuis la base quand l'utilisateur se connecte
  useEffect(() => {
    if (!user) {
      lastUserThemeRef.current = null;
      return;
    }

    const loadUserTheme = async () => {
      try {
        logger.debug(`Loading theme for: ${user.email}`);
        const supabase = await getSupabase();
        const { data, error } = await supabase
          .from('users')
          .select('industry_theme')
          .eq('email', user.email)
          .maybeSingle();

        if (error) {
          logger.warn('Error loading user theme:', error);
          return;
        }

        logger.debug('User data:', data);
        
        if (data?.industry_theme && (data.industry_theme === 'resto' || data.industry_theme === 'office' || data.industry_theme === 'nightclub')) {
          if (lastUserThemeRef.current !== data.industry_theme) {
            logger.debug(`Theme loaded from DB: ${data.industry_theme} (current: ${theme})`);
            
            const pathParts = window.location.pathname.split('/').filter(Boolean);
            const skinFromUrl = pathParts.find(part => ['resto', 'office', 'nightclub'].includes(part));
            
            if (!skinFromUrl) {
              lastUserThemeRef.current = data.industry_theme;
              setTheme(data.industry_theme);
              logger.debug(`Theme applied from DB: ${data.industry_theme}`);
            } else {
              logger.debug('Theme in URL detected, DB theme ignored');
              lastUserThemeRef.current = theme;
            }
          } else {
            logger.debug(`Theme already up to date: ${data.industry_theme}`);
          }
        } else {
          logger.debug('No theme in DB or invalid theme:', data?.industry_theme);
        }
      } catch (error) {
        logger.warn('Error loading theme:', error);
      }
    };

    // Délai pour laisser le temps à l'initialisation de se terminer
    const timeoutId = setTimeout(loadUserTheme, 100);
    return () => clearTimeout(timeoutId);
  }, [user?.email]); // Retirer theme des dépendances !

  // Sauvegarder le thème en base quand il change (et que l'utilisateur est connecté)
  useEffect(() => {
    if (!user || !theme || !isInitializedRef.current) return;
    
    // Éviter de sauvegarder si c'est le thème qu'on vient de charger
    if (lastUserThemeRef.current === theme) return;

    const saveUserTheme = async () => {
      try {
        const supabase = await getSupabase();
        // D'abord vérifier si l'utilisateur existe
        const { data: existingUser } = await supabase
          .from('users')
          .select('id')
          .eq('auth_user_id', user.id)
          .single();

        if (existingUser) {
          const { error } = await supabase
            .from('users')
            .update({
              industry_theme: theme,
              updated_at: new Date().toISOString()
            })
            .eq('auth_user_id', user.id);

          if (error) {
            logger.warn('Error saving theme:', error);
          } else {
            logger.debug(`Theme ${theme} saved for user ${user.email}`);
            lastUserThemeRef.current = theme;
          }
        } else {
          logger.warn(`User ${user.email} not found in users table. Theme saved locally only.`);
        }
      } catch (error) {
        logger.warn('Error saving theme:', error);
      }
    };

    // Délai pour éviter les sauvegardes trop fréquentes
    const timeoutId = setTimeout(saveUserTheme, 500);
    return () => clearTimeout(timeoutId);
  }, [user?.id, theme]);

  const handleSetTheme = (newTheme: 'office' | 'resto' | 'nightclub') => {
    logger.debug(`Theme change: ${theme} → ${newTheme}`);
    setTheme(newTheme);
    localStorage.setItem('komby-theme', newTheme);
  };

  const forceTheme = (newTheme: 'office' | 'resto' | 'nightclub') => {
    logger.debug(`Force theme: ${theme} → ${newTheme}`);
    setTheme(newTheme);
    localStorage.setItem('komby-theme', newTheme);
    lastUserThemeRef.current = newTheme;
  };

  // colorScheme: 'light' (Light White) | 'dark' (Dark Blue)
  // ✅ localStorage RÉACTIVÉ pour persister pendant la navigation
  const handleSetColorScheme = (newColorScheme: 'light' | 'dark') => {
    setColorScheme(newColorScheme);
    localStorage.setItem('komby-color-scheme', newColorScheme);
    
    // Appliquer le dark mode au document
    if (newColorScheme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  };

  // surface: 'black' => Dark Spatial (radial) a.k.a. "dark profond"
  // ✅ localStorage RÉACTIVÉ pour persister pendant la navigation
  const setSurface = (newSurface: 'default' | 'black') => {
    setSurfaceState(newSurface);
    localStorage.setItem('komby-surface', newSurface);
    if (newSurface === 'black') {
      document.documentElement.classList.add('theme-black');
    } else {
      document.documentElement.classList.remove('theme-black');
    }
  };

  const toggleDarkMode = () => {
    const newColorScheme = colorScheme === 'light' ? 'dark' : 'light';
    handleSetColorScheme(newColorScheme);
  };

  const value = {
    theme,
    setTheme: handleSetTheme,
    colorScheme,
    setColorScheme: handleSetColorScheme,
    isDark: colorScheme === 'dark',
    toggleDarkMode,
    forceTheme,
    surface,
    setSurface,
    isBlack: surface === 'black',
  } as const;

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};